import React, { useContext, useEffect, useRef } from "react";
import Cookie from "js-cookie";
import { Modal } from "@clarkinc/zest-design-system";
import { cls } from "cls";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { ExtendedWarranty } from "~/ProductDetails/ProductDetailPageResult";
import protectionPlansLogo from "./protection-plans-logo.svg";
import safewareLogo from "./safewareLogo.png";
import { AddToCartModal, WarrantyOption } from "~/common/AddToCartModal";
import ExtendedWarrantyModalContext from "./ExtendedWarrantyModalContext";
if (window.HTMLDialogElement === undefined) import("dialog-polyfill/dialog-polyfill.css");
type AddToCartModalWarranty = Omit<AddToCartModal<"warranty">, "isOpen">;
interface Props extends AddToCartModalWarranty {
  isThirdPartyExtendedWarrantyEligible: boolean;
  extendedWarranties: ExtendedWarranty[];
  extendedWarrantyRefs: HTMLInputElement[];
  onSubmit: (result: WarrantyOption) => void;
  selectedWarranty?: ExtendedWarranty | undefined;
}
const ExtendedWarrantyModal = ({
  extendedWarranties,
  extendedWarrantyRefs,
  isThirdPartyExtendedWarrantyEligible,
  onSubmit,
  selectedWarranty
}: Props) => {
  // Because previous OS and browser versions lack <dialog> support, we need to include a polyfill
  // We use a dynamic import to avoid React Render issues with the package
  useEffect(function dialogPolyfill() {
    let extendedWarrantyModal: HTMLDialogElement;
    if (isThirdPartyExtendedWarrantyEligible) {
      extendedWarrantyModal = document.querySelector('dialog[data-testid="thirdparty-warranty-modal"]') as HTMLDialogElement;
    } else {
      extendedWarrantyModal = document.querySelector('dialog[data-testid="inhouse-warranty-modal"]') as HTMLDialogElement;
    }
    import("dialog-polyfill").then(dialogPolyfill => {
      if (extendedWarrantyModal) {
        dialogPolyfill.default.registerDialog(extendedWarrantyModal);
      }
    }).catch(err => {
      console.error("dialog polyfill error:", err);
    });
  });
  const selectedRadioId = selectedWarranty?.id ?? 0;
  const selectedRadioIdRef = useRef(selectedRadioId);
  const {
    showDoNotDisplayOfferButton,
    setShowDoNotDisplayOfferButton,
    runAddToCartModalFunctions,
    setRunAddToCartModalFunctions,
    addToCartCloseModal,
    addToCartSubmitModal
  } = useContext(ExtendedWarrantyModalContext);
  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = event.currentTarget;
    const update = Number(value);
    onSubmit({
      warrantyId: update,
      skipCommercialModal: !update
    });
  };
  const handleOpen = () => {
    selectedRadioIdRef.current = selectedRadioId;
  };
  const handleDoNotShowOffers = (event: React.ChangeEvent<HTMLInputElement>) => {
    Cookie.set("doNotShowWarrantyModal", event.target.checked.toString());
  };
  const handleNoThanksClick = () => {
    onSubmit({
      warrantyId: 0,
      skipCommercialModal: true
    });
    if (runAddToCartModalFunctions) {
      addToCartSubmitModal({
        warrantyId: 0,
        skipCommercialModal: true,
        noThanksSelected: true
      });
      setRunAddToCartModalFunctions(false);
    }
  };
  const handleCancel = () => {
    if (selectedRadioId !== selectedRadioIdRef.current) {
      onSubmit({
        warrantyId: selectedRadioIdRef.current,
        skipCommercialModal: true
      });
    }
    if (runAddToCartModalFunctions) {
      addToCartCloseModal();
      setRunAddToCartModalFunctions(false);
    }
  };

  // When the Add Protection Button is hidden, focus lands on another appropriate control
  const handleFocus = () => {
    setShowDoNotDisplayOfferButton(false);
    const addProtectionButton = document.getElementById("addProtectionButton");
    const editProtectionButton = document.getElementById("extendedWarrantyInfoTextEditWarranty");
    if (addProtectionButton) {
      addProtectionButton?.focus();
    } else if (editProtectionButton) {
      editProtectionButton.focus();
    }
  };
  const highlightsListItemClasses = cls("icon-check", "block", "relative", "text-sm", "leading-4", "mb-2", "pl-5", "text-gray-800");
  return <Modal onOpen={handleOpen} onClose={handleFocus} headerProps={{
    className: "text-xl font-semibold",
    closeButtonProps: {
      onClick: () => {
        handleCancel();
      },
      "aria-label": "close dialog"
    }
  }} data-testid={`${isThirdPartyExtendedWarrantyEligible ? "thirdparty-warranty-modal" : "inhouse-warranty-modal"}`} id="extended-warranty-modal-dialog" hideFooter headerTitle={`${isThirdPartyExtendedWarrantyEligible ? "Commercial Product Protection Plan from Safeware" : "Extended Warranty Protection"}`} aria-label={`${isThirdPartyExtendedWarrantyEligible ? "Commercial Product Protection Plan from Safeware" : "Extended Warranty Protection"}`} theme="wss" className={cls("max-w-[600px]", "mt-[12vh]", "max-h-[85vh]")} contentProps={{
    className: cls("p-0", "h-fit")
  }} data-sentry-element="Modal" data-sentry-component="ExtendedWarrantyModal" data-sentry-source-file="ExtendedWarrantyModal.tsx">
      <div className={`${!isThirdPartyExtendedWarrantyEligible ? cls("row", "p-5") : "p-5"}`}>
        <div className={`${!isThirdPartyExtendedWarrantyEligible ? "cols-sm--7" : ""}`}>
          {!isThirdPartyExtendedWarrantyEligible ? <p className="text-sm leading-snug text-gray-800 font-normal text-left">
              <b>
                Keep your business running worry-free by extending coverage on
                this product&apos;s existing warranty, powered by Ready Kitchen
                Warranty.
              </b>{" "}
              For warranty information about this product, please refer to the
              original manufacturer warranty on this page.
            </p> : <>
              <img className="mb-4" src={safewareLogo} style={{
            maxWidth: "250px"
          }} alt="Safeware, own with confidence logo" />
              <p className="text-sm leading-snug text-gray-800 font-normal text-left">
                You just chose a mega-quality product, but coverage from
                manufacturer&apos;s warranties are limited. Safeware&apos;s
                product protection will cover all that&apos;s included in the
                manufacturer&apos;s warranty in addition to:
              </p>
              <ul className={cls("highlights", "mt-2", "text-lg", "leading-4", "!pt-0", "!border-t-0")}>
                <li className={highlightsListItemClasses}>
                  Mechanical and Electrical Failure, after the
                  manufacturer&apos;s warranty expires
                </li>
                <li className={highlightsListItemClasses}>
                  Day One Power Surge Protection
                </li>
                <li className={highlightsListItemClasses}>
                  Day One Food Spoilage Credit
                </li>
                <li className={highlightsListItemClasses}>
                  No Deductibles or Service Fees
                </li>
                <li className={highlightsListItemClasses}>
                  Backed by an AM Best A-Rated Insurer
                </li>
              </ul>
            </>}
          <fieldset className="border-0 mt-5 p-0">
            <legend className="mb-2 mt-5 font-bold text-gray-800 text-left text-lg">
              Choose your Protection Plan:
            </legend>
            <label key="No Additional Protection" className="font-normal text-sm leading-4">
              <input type="radio" name="modalProductWarranty" value="0" ref={input => input && extendedWarrantyRefs.push(input)} onChange={handleRadioButtonChange} checked={selectedRadioId === 0} data-testid="modal-Product-Warranty-0" />
              &nbsp; No Additional Protection
            </label>
            {extendedWarranties.map((warranty: ExtendedWarranty) => <label key={warranty.name} className="font-normal text-sm leading-4">
                <input type="radio" name="modalProductWarranty" value={warranty.id} ref={input => input && extendedWarrantyRefs.push(input)} onChange={handleRadioButtonChange} checked={selectedRadioId === warranty.id} data-testid={`modal-Product-Warranty-${warranty.id}`} />
                <>
                  &nbsp; {warranty.name}
                  (s)&nbsp;-&nbsp;
                  <span className="text-red-500">
                    <strong>
                      {formatAsCurrency.formatIfUndefined(warranty.price)}
                    </strong>
                  </span>
                </>
              </label>)}
          </fieldset>
        </div>
        {!isThirdPartyExtendedWarrantyEligible && <div className="cols-sm--5 flex justify-center">
            <img className="warranty-modal__img self-center" src={`${!isThirdPartyExtendedWarrantyEligible ? protectionPlansLogo : ""}`} alt="Ready Kitchen Warranty logo" />
          </div>}
        <div className="row mt-4">
          <div className="flex">
            <Modal.Trigger className="btn mr-4 mb-2" name="extendedWarrantyModalInHouseUpdateCoverageButton" data-testid="extendedWarranty-inhouse-update-coverage-button" theme="wss" htmlFor="extended-warranty-modal-dialog" type="button" onClick={runAddToCartModalFunctions ? () => {
            addToCartSubmitModal({
              warrantyId: selectedRadioId,
              skipCommercialModal: selectedRadioId === 0
            });
            setRunAddToCartModalFunctions(false);
          } : undefined} data-sentry-element="unknown" data-sentry-source-file="ExtendedWarrantyModal.tsx">
              Update Coverage
            </Modal.Trigger>
            {showDoNotDisplayOfferButton ? <Modal.Trigger className="btn mr-4 mb-2" data-testid="cancel-button" onClick={handleNoThanksClick} htmlFor="extended-warranty-modal-dialog" theme="wss" variant="white" type="button">
                No Thanks
              </Modal.Trigger> : <Modal.Trigger type="button" className="btn mb-2" data-testid="cancel-button" onClick={handleCancel} htmlFor="extended-warranty-modal-dialog" theme="wss" variant="white">
                Cancel
              </Modal.Trigger>}
          </div>

          {showDoNotDisplayOfferButton && <label className="warranty-modal__dontshow flex flex-row gap-2 items-start mt-0">
              <input onChange={handleDoNotShowOffers} type="checkbox" className="w-3 h-3 mt-[3px]" />
              <p className="flex flex-col text-[#7d8a73] mt-0 items-start text-xs font-normal leading-[1.2]">
                <span>Don't show me</span>
                <span> these offers</span>
              </p>
            </label>}
        </div>
      </div>
      <footer className={cls("modal-footer", "flex-col", "border-t-[1px]", "border-solid", "border-gray-200", "bg-gray-50", "border-x-0", "border-b-0", "p-4", "w-fit")} data-testid="modal-footer">
        {isThirdPartyExtendedWarrantyEligible ? <>
            <p className="text-left text-xs leading-tight text-neutral-700 font-normal mb-2">
              Product protection coverage begins from day one that the
              manufacturer&apos;s warranty starts. Products are only eligible
              for an extended warranty if used in a commercial setting.
              Non-commercial use may result in a loss of coverage. For more
              information on whether the item has a manufacturer&apos;s warranty
              and what is covered, please refer to the warranty information in
              our Resources Downloads section of the item&apos;s page.&nbsp;
              <a href="/ask.html" target="_blank" className="underline">
                Learn More<span aria-hidden>&nbsp;&gt;</span>
                <span className="sr-only"> opens in a new tab</span>
              </a>
            </p>
            <p className="text-left mb-0 text-xs text-neutral-700 font-normal leading-tight">
              Please see{" "}
              <a href="/uploads/design/2020/8/safeware_terms_and_conditions.pdf" target="_blank" className="underline">
                terms and conditions
                <span className="sr-only"> opens a PDF document</span>
              </a>
              &nbsp;for coverage details including limitations, exclusions and
              providers info. The Obligor in TX is National Product Care Company
              dba Texas National Product Care Company, Inc.
            </p>
          </> : <p className="text-left mb-0 text-xs text-neutral-700 font-normal leading-tight">
            Extended Warranty Protection extends the original manufacturer
            warranty; any use outside the original warranty voids the protection
            plan.
          </p>}
      </footer>
    </Modal>;
};
export default ExtendedWarrantyModal;